import { useState } from 'react';
import { getPortalDocumentContent } from '../../core/api/portal-service';
import { formatPlainDate } from '../../core/dates/plain-date';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalDocument } from '../../core/models/portal-document';
import { Button } from '../../ui/button';
import { EmptyState } from '../../ui/empty-state';
import { DocumentIcon, DownloadIcon } from '../../ui/portal-icons';

/**
 * The documents the company has shared on this case.
 *
 * **Nothing here is a link.** A case document is authorized on every request, so the download needs
 * the session and an `<a href>` sends none — the file is fetched, handed to a synthetic link and the
 * object URL revoked straight away. The visible consequence is that a failed download says so
 * instead of navigating away, which is the right behaviour when the company has just withdrawn a
 * document the customer still has on screen.
 */
export function DocumentList({ documents }: { documents: PortalDocument[] }) {
    const { t } = useTranslation();
    const language = useLanguage();
    const [busyId, setBusyId] = useState<string | null>(null);
    const [failed, setFailed] = useState<string | null>(null);

    if (documents.length === 0) {
        return (
            <EmptyState compact icon={<DocumentIcon />} title={t('portal.no_documents')}>
                {t('portal.no_documents_hint')}
            </EmptyState>
        );
    }

    async function download(document: PortalDocument) {
        setBusyId(document.id);
        setFailed(null);

        try {
            const blob = await getPortalDocumentContent(document.id, document.versionId);
            const url = URL.createObjectURL(blob);
            const link = window.document.createElement('a');

            link.href = url;
            link.download = document.fileName;
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            // Almost always a document withdrawn between the list being drawn and the click. The
            // message says to refresh rather than naming a status nobody can act on.
            setFailed(document.id);
        } finally {
            setBusyId(null);
        }
    }

    return (
        <ul className="divide-y divide-slate-200/80 dark:divide-slate-800">
            {documents.map((document) => (
                <li
                    key={document.id}
                    className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        <DocumentIcon />
                    </span>

                    <div className="min-w-0 flex-1 basis-40">
                        <p className="font-semibold break-words text-slate-900 dark:text-slate-100">
                            {document.title}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {formatPlainDate(document.sharedAt.slice(0, 10), language)} ·{' '}
                            {formatSize(document.sizeBytes)}
                        </p>

                        {failed === document.id && (
                            <p role="alert" className="mt-1 text-sm text-rose-700 dark:text-rose-400">
                                {t('portal.document_unavailable')}
                            </p>
                        )}
                    </div>

                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void download(document)}
                        disabled={busyId === document.id}
                    >
                        <DownloadIcon className="size-4" />
                        {busyId === document.id
                            ? t('portal.document_downloading')
                            : t('portal.document_download')}
                    </Button>
                </li>
            ))}
        </ul>
    );
}

/**
 * Whole kilobytes or megabytes, with no locale-aware number formatting — a size is a hint about
 * whether this will take a moment on a phone, not a figure anyone reads twice.
 */
function formatSize(bytes: number): string {
    return bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
